/**
 * Voxel theme setup function.
 * Generates a 3D Minecraft-style world with terrain, mountains, and trees.
 */
import type { ThemeSetupFn } from "../types";

// ============================================================================
// Utility Functions
// ============================================================================

const randomInt = (min: number, max: number): number => {
  return Math.floor(Math.random() * (max - min + 1)) + min;
};

const pickRandom = <T>(arr: T[]): T => arr[randomInt(0, arr.length - 1)];

// Generate a 16x16 SVG texture as a data URI
const createBlockTexture = (getPixelColor: (x: number, y: number) => string): string => {
  const pixels: string[] = [];
  let bgColor = "";
  for (let y = 0; y < 16; y++) {
    for (let x = 0; x < 16; x++) {
      const color = getPixelColor(x, y);
      // Use first pixel as background color
      if (x === 0 && y === 0) {
        bgColor = color;
      }
      pixels.push(`<rect x="${x}" y="${y}" width="1" height="1" fill="${color}"/>`);
    }
  }
  // Add background rect first, then pixel rects on top
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" shape-rendering="crispEdges"><rect width="16" height="16" fill="${bgColor}"/>${pixels.join(
    ""
  )}</svg>`;
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
};

// ============================================================================
// Block Texture Generators - TOP Faces
// ============================================================================

// Grass block TOP - all green varied pixels
const createGrassTopTexture = (): string => {
  const grassColors = ["#5D9B38", "#4A8C2C", "#3C7A24", "#6AAA42", "#4E9030", "#5B9636"];
  return createBlockTexture(() => pickRandom(grassColors));
};

// Dirt block - varied browns (same for all faces)
const createDirtTexture = (): string => {
  const dirtColors = ["#8B6B4A", "#7A5C3D", "#6B4D32", "#9A7858", "#5C4428", "#6D5238", "#7E5F40"];
  const accentColors = ["#4A5568", "#3D4A5C"];
  return createBlockTexture(() => {
    if (Math.random() < 0.05) {
      return pickRandom(accentColors);
    }
    return pickRandom(dirtColors);
  });
};

// Stone block - varied grays (same for all faces)
const createStoneTexture = (): string => {
  const stoneColors = ["#7A7A7A", "#8A8A8A", "#6A6A6A", "#9A9A9A", "#5A5A5A", "#707070", "#808080"];
  const darkAccents = ["#4A4A4A", "#3A3A3A"];
  return createBlockTexture(() => {
    if (Math.random() < 0.1) {
      return pickRandom(darkAccents);
    }
    return pickRandom(stoneColors);
  });
};

// Oak Log TOP - concentric ring pattern
const createLogTopTexture = (): string => {
  const ringColors = ["#6B5035", "#5A4028", "#7A6040", "#4A3520"];
  const centerColor = "#3A2818";
  return createBlockTexture((x, y) => {
    const cx = 7.5,
      cy = 7.5;
    const dist = Math.sqrt((x - cx) ** 2 + (y - cy) ** 2);
    if (dist < 2) {
      return centerColor;
    }
    if (dist < 4) {
      return ringColors[0];
    }
    if (dist < 6) {
      return ringColors[1];
    }
    if (dist < 8) {
      return ringColors[2];
    }
    return ringColors[3];
  });
};

// Leaves - varied greens
const createLeavesTexture = (): string => {
  const leafColors = ["#3A5F0B", "#4A7A15", "#2D4A08", "#5A8A25", "#3F6B10"];
  const darkColors = ["#1A3005", "#2A4008"];
  return createBlockTexture(() => {
    if (Math.random() < 0.15) {
      return pickRandom(darkColors);
    }
    return pickRandom(leafColors);
  });
};

// ============================================================================
// Block Texture Generators - SIDE Faces
// ============================================================================

// Grass block SIDE - dirt with green grass strip at top
const createGrassSideTexture = (): string => {
  const grassColors = ["#5D9B38", "#4A8C2C", "#3C7A24", "#6AAA42"];
  const dirtColors = ["#8B6B4A", "#7A5C3D", "#6B4D32", "#9A7858", "#5C4428"];
  return createBlockTexture((_, y) => {
    if (y < 2) {
      return pickRandom(grassColors);
    }
    if (y === 2) {
      return Math.random() > 0.5 ? pickRandom(grassColors) : pickRandom(dirtColors);
    }
    return pickRandom(dirtColors);
  });
};

// Oak Log SIDE - bark texture with vertical continuity
const createLogSideTexture = (): string => {
  const baseColors = ["#9C8559", "#8B7750", "#A89060", "#7A6242"];
  const grooveColor = "#5C4830";

  // Pre-generate all pixels with vertical continuity
  // Each pixel has 70% chance to match the pixel above it
  const pixelColors: string[][] = [];
  for (let x = 0; x < 16; x++) {
    pixelColors[x] = [];
    for (let y = 0; y < 16; y++) {
      if (y > 0 && Math.random() < 0.7) {
        // 70% chance to be the same color as the pixel above
        pixelColors[x][y] = pixelColors[x][y - 1];
      } else if (x % 2 === 0) {
        // No grooves for even columns
        pixelColors[x][y] = pickRandom(baseColors);
      } else {
        // Odd columns: 40% chance to be a groove
        if (Math.random() < 0.4) {
          pixelColors[x][y] = grooveColor;
        } else {
          pixelColors[x][y] = pickRandom(baseColors);
        }
      }
    }
  }

  return createBlockTexture((x, y) => pixelColors[x][y]);
};

// Leaves SIDE - slightly darker
const createLeavesSideTexture = (): string => {
  const leafColors = ["#2D4A08", "#3A5F0B", "#254005", "#4A7A15"];
  const darkColors = ["#1A3005", "#152805"];
  return createBlockTexture(() => {
    if (Math.random() < 0.2) {
      return pickRandom(darkColors);
    }
    return pickRandom(leafColors);
  });
};

// ============================================================================
// Block Types and Texture Mapping
// ============================================================================

type BlockType = "grass" | "dirt" | "stone" | "log" | "leaves";

const getTopTexture = (type: BlockType): string => {
  switch (type) {
    case "grass":
      return createGrassTopTexture();
    case "dirt":
      return createDirtTexture();
    case "stone":
      return createStoneTexture();
    case "log":
      return createLogTopTexture();
    case "leaves":
      return createLeavesTexture();
  }
};

const getSideTexture = (type: BlockType): string => {
  switch (type) {
    case "grass":
      return createGrassSideTexture();
    case "dirt":
      return createDirtTexture();
    case "stone":
      return createStoneTexture();
    case "log":
      return createLogSideTexture();
    case "leaves":
      return createLeavesSideTexture();
  }
};

// ============================================================================
// 3D Block Dimensions
// ============================================================================

const BLOCK_WIDTH = 48;
const BLOCK_HEIGHT = 32;
const TOP_HEIGHT = 16; // Height of the top face (creates 3D effect)

// ============================================================================
// Main Setup Function
// ============================================================================

export const voxelSetup: ThemeSetupFn = (doc, win) => {
  // Add Minecraft typeface
  const fontLink = doc.createElement("link");
  fontLink.rel = "stylesheet";
  fontLink.href = "https://cdn.jsdelivr.net/npm/typeface-minecraft@1.0.0/index.min.css";
  doc.head.appendChild(fontLink);

  // Create main container
  const container = doc.createElement("div");
  container.className = "voxel-theme--blocks-container";
  doc.body.insertBefore(container, doc.body.firstChild);

  // ============================================================================
  // Sky Gradient Background (not scaled - always fills viewport)
  // ============================================================================
  const sky = doc.createElement("div");
  sky.className = "voxel-sky";
  sky.style.cssText = `
    position: absolute;
    inset: 0;
    background: linear-gradient(180deg,
      #4A90C2 0%,
      #87CEEB 30%,
      #B0E2FF 60%,
      #E0F4FF 100%
    );
    z-index: -3;
  `;
  container.appendChild(sky);

  // ============================================================================
  // Scaled Scene Container - scales to always fill 100vw
  // ============================================================================
  const REFERENCE_WIDTH = 1920; // Generate scene at this width
  const sceneWidth = REFERENCE_WIDTH;

  const sceneContainer = doc.createElement("div");
  sceneContainer.className = "voxel-scene";

  // Update scale based on viewport width
  const updateScale = () => {
    const scale = win.innerWidth / REFERENCE_WIDTH;
    sceneContainer.style.cssText = `
      position: absolute;
      bottom: 0;
      left: 0;
      width: ${REFERENCE_WIDTH}px;
      height: 100%;
      transform: scale(${scale});
      transform-origin: bottom left;
      z-index: -2;
    `;
  };
  updateScale();

  // Listen for resize to update scale
  win.addEventListener("resize", updateScale);
  container.appendChild(sceneContainer);

  // ============================================================================
  // Distant Mountains (Background Layer) - rendered to canvas for efficiency
  // ============================================================================
  const mountainBlockSize = 16;
  const mountainCanvasHeight = 600;
  const mountainCanvas = doc.createElement("canvas");
  mountainCanvas.className = "voxel-mountains";
  mountainCanvas.width = sceneWidth;
  mountainCanvas.height = mountainCanvasHeight;
  mountainCanvas.style.cssText = `
    position: absolute;
    bottom: 0;
    left: 0;
    width: ${sceneWidth}px;
    height: ${mountainCanvasHeight}px;
    image-rendering: pixelated;
  `;
  sceneContainer.appendChild(mountainCanvas);

  const ctx = mountainCanvas.getContext("2d");
  if (ctx) {
    // Set global opacity for distant atmospheric effect
    ctx.globalAlpha = 0.6;

    // Generate mountain heights
    const numMountainCols = Math.ceil(sceneWidth / mountainBlockSize) + 1;
    const mountainHeights: number[] = [];

    // Base mountain terrain (rolling hills)
    let currentMtnHeight = randomInt(10, 14);
    for (let i = 0; i < numMountainCols; i++) {
      mountainHeights.push(currentMtnHeight);
      const change = randomInt(-1, 1);
      currentMtnHeight = Math.max(7, Math.min(18, currentMtnHeight + change));
    }

    // Add mountain peaks (4-7 peaks)
    const numPeaks = randomInt(4, 7);
    for (let p = 0; p < numPeaks; p++) {
      const peakCol = randomInt(10, numMountainCols - 10);
      const peakHeight = randomInt(24, 36);
      for (let i = -8; i <= 8; i++) {
        const col = peakCol + i;
        if (col >= 0 && col < numMountainCols) {
          const h = peakHeight - Math.abs(i) * 2;
          if (h > mountainHeights[col]) {
            mountainHeights[col] = h;
          }
        }
      }
    }

    // Color palettes for mountains
    const topColors = ["#4A6B5A", "#3A5B4A", "#5A7B6A", "#3D5240"];
    const bodyColors = ["#5A6A7A", "#4A5A6A", "#6A7A8A", "#4D5D6D"];

    // Draw mountains - canvas y=0 is top, so we draw from top down
    for (let col = 0; col < numMountainCols; col++) {
      const height = mountainHeights[col];
      for (let h = 0; h < height; h++) {
        const x = col * mountainBlockSize;
        // Convert from bottom-up to canvas top-down coordinates
        const y = mountainCanvasHeight - (h + 1) * mountainBlockSize;

        // Top block gets different color
        const isTop = h === height - 1;
        const colors = isTop ? topColors : bodyColors;
        ctx.fillStyle = pickRandom(colors);
        ctx.fillRect(x, y, mountainBlockSize, mountainBlockSize);
      }
    }
  }

  // ============================================================================
  // Parallax constants (needed before terrain renders)
  // Based on scroll percentage (0% = top, 100% = bottom)
  // ============================================================================
  const MOUNTAIN_MAX_TRANSLATE = 15; // Mountains move less (distant)
  const TERRAIN_MAX_TRANSLATE = 40; // Terrain moves more (close)

  // Start mountain offset immediately
  mountainCanvas.style.transform = `translateY(${MOUNTAIN_MAX_TRANSLATE}px)`;

  // Parallax update function (will be set up after terrain renders)
  let updateParallax: (() => void) | null = null;

  // Click handler cleanup (will be set up after terrain renders)
  let cleanupClickHandler: (() => void) | null = null;

  // ============================================================================
  // Deferred Terrain Rendering - runs when browser is idle
  // ============================================================================
  const deferFn = win.requestIdleCallback ?? ((fn: () => void) => win.setTimeout(fn, 1));
  const cancelDeferFn = win.cancelIdleCallback ?? ((id: number) => win.clearTimeout(id));

  const idleCallbackId = deferFn(() => {
    // Create terrain container (starts invisible for fade-in)
    const terrainContainer = doc.createElement("div");
    terrainContainer.className = "voxel-terrain";
    terrainContainer.style.cssText = `
      position: absolute;
      bottom: 0;
      left: 0;
      width: 100%;
      height: 400px;
      z-index: 1;
      transform: translateY(${TERRAIN_MAX_TRANSLATE}px);
      opacity: 0;
      transition: opacity 0.5s ease-in-out;
    `;
    sceneContainer.appendChild(terrainContainer);

    // Calculate terrain grid
    const numCols = Math.ceil(sceneWidth / BLOCK_WIDTH) + 2;

    // Generate terrain heights
    const terrainHeights: number[] = [];
    let height = randomInt(2, 4);
    for (let col = 0; col < numCols; col++) {
      terrainHeights.push(height);
      const change = randomInt(-1, 1);
      height = Math.max(1, Math.min(4, height + change));
    }

    // Pre-calculate tree positions BEFORE terrain rendering
    // This allows us to render dirt instead of grass under trees
    const treePositions: number[] = [];
    for (let col = 0; col < numCols; col++) {
      if (col <= 2 || col >= numCols - 3) {
        // No trees too close to the edges of the viewport
        continue;
      }
      // No trees in the middle 50% of columns (since they are obscured by dashboard content)
      if (col >= numCols / 4 && col <= (numCols / 4) * 3) {
        continue;
      }
      if (Math.random() < 0.3) {
        const tooClose = treePositions.some(pos => Math.abs(pos - col) < 4);
        if (!tooClose) {
          treePositions.push(col);
        }
      }
    }

    // Create 3D block helper
    // z-index for isometric: blocks to the RIGHT are in front, blocks HIGHER are in front
    // (camera is at front-left-above, looking toward back-right-down)
    const create3DBlock = (
      x: number,
      bottomPos: number,
      blockType: BlockType,
      showTop = true,
      showRight = true
    ): HTMLElement => {
      const col = Math.floor(x / BLOCK_WIDTH);
      const row = Math.floor(bottomPos / BLOCK_HEIGHT);
      const zIndex = col * 10 + row;

      const block = doc.createElement("div");
      block.className = "voxel-3d-block";
      block.dataset.col = String(col);
      block.dataset.row = String(row);
      block.dataset.blockType = blockType;
      block.style.cssText = `
        position: absolute;
        left: ${x}px;
        bottom: ${bottomPos}px;
        width: ${BLOCK_WIDTH + (showRight ? TOP_HEIGHT : 0)}px;
        height: ${BLOCK_HEIGHT + (showTop ? TOP_HEIGHT : 0)}px;
        z-index: ${zIndex};
      `;

      if (showTop) {
        const topFace = doc.createElement("div");
        topFace.className = "voxel-block-top";
        topFace.style.cssText = `
          position: absolute;
          top: 0;
          left: 0;
          width: ${BLOCK_WIDTH}px;
          height: ${TOP_HEIGHT}px;
          background-image: ${getTopTexture(blockType)};
          background-size: cover;
          image-rendering: pixelated;
          transform: skewX(-45deg);
          transform-origin: bottom left;
        `;
        block.appendChild(topFace);
      }

      const frontFace = doc.createElement("div");
      frontFace.className = "voxel-block-front";
      frontFace.style.cssText = `
        position: absolute;
        top: ${showTop ? TOP_HEIGHT : 0}px;
        left: 0;
        width: ${BLOCK_WIDTH}px;
        height: ${BLOCK_HEIGHT}px;
        background-image: ${getSideTexture(blockType)};
        background-size: cover;
        image-rendering: pixelated;
      `;
      block.appendChild(frontFace);

      if (showRight) {
        const rightFace = doc.createElement("div");
        rightFace.className = "voxel-block-right";
        rightFace.style.cssText = `
          position: absolute;
          top: ${showTop ? TOP_HEIGHT : 0}px;
          left: ${BLOCK_WIDTH}px;
          width: ${TOP_HEIGHT}px;
          height: ${BLOCK_HEIGHT}px;
          background-image: ${getSideTexture(blockType)};
          background-size: cover;
          image-rendering: pixelated;
          transform: skewY(-45deg);
          transform-origin: top left;
          filter: brightness(0.7);
        `;
        block.appendChild(rightFace);
      }

      return block;
    };

    // Render terrain blocks
    for (let col = 0; col < numCols; col++) {
      const colHeight = terrainHeights[col];

      for (let h = 0; h < colHeight; h++) {
        const x = col * BLOCK_WIDTH;
        const bottomPos = h * BLOCK_HEIGHT;
        const showTop = h === colHeight - 1;

        let blockType: BlockType;
        if (h === colHeight - 1) {
          // Top block: dirt if there's a tree here, otherwise grass
          blockType = treePositions.includes(col) ? "dirt" : "grass";
        } else if (h >= colHeight - 3) {
          blockType = "dirt";
        } else {
          blockType = "stone";
        }

        const block = create3DBlock(x, bottomPos, blockType, showTop);
        terrainContainer.appendChild(block);
      }

      // Add tree if this column was pre-selected for a tree
      if (treePositions.includes(col)) {
        const treeX = col * BLOCK_WIDTH;
        const treeBottomStart = colHeight * BLOCK_HEIGHT;

        const trunkHeight = randomInt(3, 4);
        for (let t = 0; t < trunkHeight; t++) {
          const logBottom = treeBottomStart + t * BLOCK_HEIGHT;
          const logBlock = create3DBlock(treeX, logBottom, "log", t === trunkHeight - 1);
          terrainContainer.appendChild(logBlock);
        }

        const leafBottomStart = treeBottomStart + trunkHeight * BLOCK_HEIGHT;
        const leafPositions = [
          { dx: 0, dh: 0 },
          { dx: -1, dh: 0 },
          { dx: 1, dh: 0 },
          { dx: 0, dh: 1 },
          { dx: -1, dh: 1 },
          { dx: 1, dh: 1 },
          { dx: 0, dh: 2 },
        ];

        for (const pos of leafPositions) {
          const leafX = treeX + pos.dx * BLOCK_WIDTH;
          const leafBottom = leafBottomStart + pos.dh * BLOCK_HEIGHT;
          if (leafX >= 0) {
            const leafBlock = create3DBlock(leafX, leafBottom, "leaves", true);
            terrainContainer.appendChild(leafBlock);
          }
        }
      }
    }

    // Fade in the terrain after all blocks are added
    requestAnimationFrame(() => {
      terrainContainer.style.opacity = "1";
    });

    // Set up parallax scroll effect now that terrain exists
    updateParallax = () => {
      const scrollTop = win.scrollY || doc.documentElement.scrollTop;
      const scrollHeight = doc.documentElement.scrollHeight;
      const clientHeight = doc.documentElement.clientHeight;
      const maxScroll = scrollHeight - clientHeight;

      // Scroll percentage: 0 at top, 1 at bottom
      const scrollPercent = maxScroll > 0 ? Math.min(scrollTop / maxScroll, 1) : 0;

      // Layers start offset (at top) and move to 0 (at bottom)
      const mountainOffset = MOUNTAIN_MAX_TRANSLATE * (1 - scrollPercent);
      const terrainOffset = TERRAIN_MAX_TRANSLATE * (1 - scrollPercent);

      mountainCanvas.style.transform = `translateY(${mountainOffset}px)`;
      terrainContainer.style.transform = `translateY(${terrainOffset}px)`;
    };

    win.addEventListener("scroll", updateParallax);

    // ========================================================================
    // Easter Egg: Click to place grass blocks
    // Click on empty space above terrain to add blocks
    // ========================================================================
    const MAX_TERRAIN_HEIGHT = 10;

    const handleDocumentClick = (e: MouseEvent) => {
      // Only handle clicks on the background (body or container elements)
      const target = e.target as HTMLElement;
      if (!target) {
        return;
      }

      // Skip if clicking on interactive UI elements
      const isUIElement =
        target.closest("button") ||
        target.closest("a") ||
        target.closest("input") ||
        target.closest("[role='button']") ||
        target.closest("[data-interactive]") ||
        target.closest(".dashboard-block"); // Skip dashboard blocks
      if (isUIElement) {
        return;
      }

      // Get click position relative to viewport bottom
      const rect = terrainContainer.getBoundingClientRect();
      const scale = win.innerWidth / REFERENCE_WIDTH;

      // Convert to unscaled terrain coordinates
      const clickX = (e.clientX - rect.left) / scale;
      const clickY = (rect.bottom - e.clientY) / scale; // Distance from bottom of terrain container

      // Determine which column was clicked
      const col = Math.floor(clickX / BLOCK_WIDTH);
      if (col < 0 || col >= terrainHeights.length) {
        return;
      }

      const currentHeight = terrainHeights[col];
      if (currentHeight >= MAX_TERRAIN_HEIGHT) {
        return;
      }

      // Check if click is in the "placement zone"
      // Zone includes the top face of the current block AND the space above it
      const topFaceBottom = currentHeight * BLOCK_HEIGHT; // Where the top face starts
      const nextBlockTop = (currentHeight + 1) * BLOCK_HEIGHT + TOP_HEIGHT;

      if (clickY >= topFaceBottom && clickY < nextBlockTop) {
        // Find the previous top block (will become dirt)
        const previousTopRow = currentHeight - 1;
        const previousTopBlock = terrainContainer.querySelector(
          `[data-col="${col}"][data-row="${previousTopRow}"][data-block-type="grass"]`
        );

        // Place new grass block with fade-in animation
        const newBlock = create3DBlock(
          col * BLOCK_WIDTH,
          currentHeight * BLOCK_HEIGHT,
          "grass",
          true
        );
        newBlock.style.opacity = "0";
        newBlock.style.transition = "opacity 0.3s ease-in-out";
        terrainContainer.appendChild(newBlock);

        // Fade in
        requestAnimationFrame(() => {
          newBlock.style.opacity = "1";
        });

        // Update height tracking
        terrainHeights[col] = currentHeight + 1;

        // After 4-8 seconds, turn the previous grass block into dirt using crossfade
        if (previousTopBlock) {
          const delay = 4000 + Math.random() * 4000; // 4-8 seconds
          win.setTimeout(() => {
            // Update block type data attribute
            (previousTopBlock as HTMLElement).dataset.blockType = "dirt";

            // Crossfade helper: overlay new texture and fade it in
            const crossfadeFace = (face: HTMLElement, newTexture: string) => {
              const fadeDuration = 200;
              const overlay = face.cloneNode(false) as HTMLElement;
              overlay.style.backgroundImage = newTexture;
              overlay.style.opacity = "0";
              overlay.style.transition = `opacity ${fadeDuration}ms ease-in-out`;
              face.parentElement?.appendChild(overlay);
              requestAnimationFrame(() => {
                overlay.style.opacity = "1";
              });
              win.setTimeout(() => {
                face.style.backgroundImage = newTexture;
                overlay.remove();
              }, fadeDuration);
            };

            // Update top face texture (if it has one)
            const topFace = previousTopBlock.querySelector(".voxel-block-top");
            if (topFace) {
              crossfadeFace(topFace as HTMLElement, getTopTexture("dirt"));
            }

            // Update front face texture
            const frontFace = previousTopBlock.querySelector(".voxel-block-front");
            if (frontFace) {
              crossfadeFace(frontFace as HTMLElement, getSideTexture("dirt"));
            }

            // Update right face texture (if it has one)
            const rightFace = previousTopBlock.querySelector(".voxel-block-right");
            if (rightFace) {
              crossfadeFace(rightFace as HTMLElement, getSideTexture("dirt"));
            }
          }, delay);
        }
      }
    };

    doc.addEventListener("click", handleDocumentClick);

    // Store cleanup for click handler
    cleanupClickHandler = () => {
      doc.removeEventListener("click", handleDocumentClick);
    };
  });

  // Return cleanup function
  return () => {
    cancelDeferFn(idleCallbackId);
    win.removeEventListener("resize", updateScale);
    if (updateParallax) {
      win.removeEventListener("scroll", updateParallax);
    }
    if (cleanupClickHandler) {
      cleanupClickHandler();
    }
    fontLink.remove();
    container.remove();
  };
};
